import { useTranslation } from "react-i18next";
import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import { createNewUserAccount, isOrgFieldUnique } from "./firestoreService";
import {
  Box,
  Button,
  Container,
  TextField,
  Typography,
  CircularProgress,
  Alert,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Collapse,
  Link,
  IconButton,
  InputAdornment,
  Select,
  MenuItem,
  InputLabel,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import {
  SUPPORTED_LANGUAGES,
  SUPPORTED_LANGUAGE_CODES,
  DEFAULT_LANGUAGE,
} from "./constants/languages";

// Three signup paths. Personal/Team naming reflects the post-modernization
// product positioning (signup-flow-modernization branch): both PRO paths
// self-serve, no SU step. Personal PRO → role=pro_user/tier=personal;
// Team PRO → role=admin/tier=team. FREE is unchanged in shape.
const SignUpOption = {
  FREE_USER: "FREE_USER",
  PERSONAL_PRO: "PERSONAL_PRO",
  TEAM_PRO: "TEAM_PRO",
};

// Debounce helper for live uniqueness checks
function debounce(func, delay) {
  let timeout;
  return function (...args) {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), delay);
  };
}

function SignUp() {
  const navigate = useNavigate();
  const [selectedOption, setSelectedOption] = useState(SignUpOption.FREE_USER);
  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [requestedOrgName, setRequestedOrgName] = useState("");
  const [requestedOrgShortName, setRequestedOrgShortName] = useState("");
  const [primaryGuideline, setPrimaryGuideline] = useState("EUCAST");
  const [isCheckingOrgName, setIsCheckingOrgName] = useState(false);
  const [isOrgNameAvailable, setIsOrgNameAvailable] = useState(null);
  const [isCheckingShortName, setIsCheckingShortName] = useState(false);
  const [isShortNameAvailable, setIsShortNameAvailable] = useState(null);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [confirmPasswordVisible, setConfirmPasswordVisible] = useState(false);

  const widgetRef = useRef(null);
  const [captchaToken, setCaptchaToken] = useState("");
  const siteKey = process.env.REACT_APP_CONTACT_RECAPTCHA_SITE_KEY;

  const { t, i18n } = useTranslation();

  // Initial org language = current UI language if it's in our supported set,
  // otherwise English. The PRO branches show this as an editable dropdown;
  // FREE captures it silently as the user's personal preference.
  const [orgLanguage, setOrgLanguage] = useState(() =>
    SUPPORTED_LANGUAGE_CODES.includes(i18n.language)
      ? i18n.language
      : DEFAULT_LANGUAGE,
  );

  useEffect(() => {
    let mounted = true;

    const tryRender = () => {
      if (!mounted || !window.grecaptcha?.enterprise) return;

      // MODIFIED: Use a unique ID for the signup form's captcha element
      const el = document.getElementById("signup-recaptcha");
      if (!el) return;

      if (
        widgetRef.current !== null &&
        typeof widgetRef.current !== "undefined"
      )
        return;

      widgetRef.current = window.grecaptcha.enterprise.render(el, {
        sitekey: siteKey,
        size: "normal",
        theme: "light",
        callback: (token) => setCaptchaToken(token),
        "expired-callback": () => setCaptchaToken(""),
        "error-callback": () => setCaptchaToken(""),
      });
    };

    const i = setInterval(() => {
      if (window.grecaptcha?.enterprise) {
        clearInterval(i);
        window.grecaptcha.enterprise.ready(tryRender);
      }
    }, 200);

    return () => {
      mounted = false;
      clearInterval(i);
    };
  }, [siteKey]);

  useEffect(() => {
    // We only check if the user has started typing in the second field
    if (confirmPassword && password !== confirmPassword) {
      setPasswordError(t("auth.passwordMismatch"));
    } else {
      setPasswordError(""); // Clear the error if they match
    }
  }, [password, confirmPassword, t]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const checkOrgNameAvailability = useCallback(
    debounce(async (name) => {
      const trimmed = (name || "").trim();
      if (trimmed.length < 2) {
        setIsOrgNameAvailable(null);
        return;
      }
      setIsCheckingOrgName(true);
      try {
        const isUnique = await isOrgFieldUnique("name", trimmed);
        setIsOrgNameAvailable(isUnique);
      } catch (err) {
        setIsOrgNameAvailable(null);
      } finally {
        setIsCheckingOrgName(false);
      }
    }, 500),
    [],
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const checkShortNameAvailability = useCallback(
    debounce(async (name) => {
      const trimmed = (name || "").trim();
      if (trimmed.length < 2) {
        setIsShortNameAvailable(null);
        return;
      }
      setIsCheckingShortName(true);
      try {
        const isUnique = await isOrgFieldUnique("shortName", trimmed);
        setIsShortNameAvailable(isUnique);
      } catch (err) {
        setIsShortNameAvailable(null);
      } finally {
        setIsCheckingShortName(false);
      }
    }, 500),
    [],
  );

  const handleOrgNameChange = (e) => {
    const value = e.target.value;
    setRequestedOrgName(value);
    setIsOrgNameAvailable(null);
    checkOrgNameAvailability(value);
  };

  const handleShortNameChange = (e) => {
    const value = e.target.value.slice(0, 25);
    setRequestedOrgShortName(value);
    setIsShortNameAvailable(null);
    checkShortNameAvailability(value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    // --- ADDED: reCAPTCHA token validation before submitting ---
    if (!captchaToken) {
      setError(t("signup.captchaRequired"));
      setIsLoading(false);
      return;
    }

    if (passwordError) {
      setIsLoading(false);
      return;
    }

    // Both PRO flows collect org name + short name with live uniqueness check.
    const needsOrgNames =
      selectedOption === SignUpOption.PERSONAL_PRO ||
      selectedOption === SignUpOption.TEAM_PRO;
    if (needsOrgNames) {
      // Sync re-check at submit time in case the debounced live check
      // hadn't fired yet (user clicked submit faster than 500ms after
      // typing). null state means "never checked"; we resolve it here.
      let orgNameOk = isOrgNameAvailable;
      if (orgNameOk === null && requestedOrgName.trim()) {
        try {
          orgNameOk = await isOrgFieldUnique("name", requestedOrgName.trim());
          setIsOrgNameAvailable(orgNameOk);
        } catch (err) {
          setError(t("signup.orgNameNotAvailable"));
          setIsLoading(false);
          return;
        }
      }
      if (orgNameOk === false) {
        setError(t("signup.orgNameNotAvailable"));
        setIsLoading(false);
        return;
      }
      if (!orgNameOk) {
        setError(t("signup.orgNameNotAvailable"));
        setIsLoading(false);
        return;
      }

      let shortNameOk = isShortNameAvailable;
      if (shortNameOk === null && requestedOrgShortName.trim()) {
        try {
          shortNameOk = await isOrgFieldUnique(
            "shortName",
            requestedOrgShortName.trim(),
          );
          setIsShortNameAvailable(shortNameOk);
        } catch (err) {
          setError(t("signup.shortNameNotAvailable"));
          setIsLoading(false);
          return;
        }
      }
      if (shortNameOk === false) {
        setError(t("signup.shortNameNotAvailable"));
        setIsLoading(false);
        return;
      }
      if (!shortNameOk) {
        setError(t("signup.shortNameNotAvailable"));
        setIsLoading(false);
        return;
      }
    }

    try {
      // All three signup paths (Free, Personal PRO, Team PRO) collect the
      // user's own password at signup — the legacy SU-sets-password flow
      // for Team PRO is gone with the activation pipeline.
      const finalPassword = password;

      const accountData = {
        displayName,
        email,
        signUpOption: selectedOption,
        password: finalPassword,
        requestedOrgName: needsOrgNames ? requestedOrgName : null,
        requestedOrgShortName: needsOrgNames ? requestedOrgShortName : null,
        // Both PRO branches collect a guideline at signup; Free has no
        // per-user org so it stays null.
        primaryGuideline: needsOrgNames ? primaryGuideline : null,
        // Org language for PRO branches (from dropdown); user-only
        // preference captured silently for FREE from current UI language.
        preferredLanguage: orgLanguage,
        recaptchaToken: captchaToken, // MODIFIED: Pass the token to the backend
      };

      await createNewUserAccount(accountData);

      navigate("/login", {
        state: { successMessage: t("signup.activationSent") },
      });
    } catch (err) {
      setError(err.message || "An unknown error occurred.");
      // MODIFIED: Reset captcha on error so user can retry
      if (widgetRef.current != null && window.grecaptcha?.enterprise) {
        window.grecaptcha.enterprise.reset(widgetRef.current);
      }
      setCaptchaToken("");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container component="main" maxWidth="xs">
      <Box
        sx={{
          marginTop: 8,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Typography component="h1" variant="h5">
          {t("signup.title")}
        </Typography>
        <Typography variant="body2" sx={{ mt: 2, textAlign: "center" }}>
          <Link
            component={RouterLink}
            to="/compare"
            sx={{ cursor: "pointer" }}
          >
            {t("signup.compareLink")}
          </Link>
        </Typography>
        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3 }}>
          <FormControl component="fieldset" margin="normal">
            <FormLabel component="legend">{t("signup.chooseOption")}</FormLabel>
            <RadioGroup
              row
              value={selectedOption}
              onChange={(e) => setSelectedOption(e.target.value)}
            >
              <FormControlLabel
                value={SignUpOption.FREE_USER}
                control={<Radio />}
                label={t("signup.optionFree")}
              />
              <FormControlLabel
                value={SignUpOption.PERSONAL_PRO}
                control={<Radio />}
                disabled
                label={`${t("signup.optionPro")} *`}
              />
              <FormControlLabel
                value={SignUpOption.TEAM_PRO}
                control={<Radio />}
                disabled
                label={`${t("signup.optionOrg")} *`}
              />
            </RadioGroup>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mt: 0.5 }}
            >
              {t("signup.proComingSoon")}
            </Typography>
          </FormControl>

          <TextField
            label={t("signup.fullName")}
            required
            fullWidth
            margin="normal"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            disabled={isLoading}
          />
          <TextField
            label={t("auth.emailAddress")}
            type="email"
            required
            fullWidth
            margin="normal"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={isLoading}
          />

          <Collapse
            in={
              selectedOption === SignUpOption.PERSONAL_PRO ||
              selectedOption === SignUpOption.TEAM_PRO ||
              selectedOption === SignUpOption.FREE_USER
            }
          >
            <TextField
              label={t("signup.createPassword")}
              type={passwordVisible ? "text" : "password"}
              required={
                selectedOption === SignUpOption.PERSONAL_PRO ||
                selectedOption === SignUpOption.TEAM_PRO ||
                selectedOption === SignUpOption.FREE_USER
              }
              fullWidth
              margin="normal"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={isLoading}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setPasswordVisible(!passwordVisible)}
                      edge="end"
                    >
                      {passwordVisible ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label={t("auth.confirmPassword")}
              type={confirmPasswordVisible ? "text" : "password"}
              required={
                selectedOption === SignUpOption.PERSONAL_PRO ||
                selectedOption === SignUpOption.TEAM_PRO ||
                selectedOption === SignUpOption.FREE_USER
              }
              fullWidth
              margin="normal"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              disabled={isLoading}
              error={!!passwordError}
              helperText={passwordError}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() =>
                        setConfirmPasswordVisible(!confirmPasswordVisible)
                      }
                      edge="end"
                    >
                      {confirmPasswordVisible ? (
                        <VisibilityOff />
                      ) : (
                        <Visibility />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Collapse>

          <Collapse
            in={
              selectedOption === SignUpOption.PERSONAL_PRO ||
              selectedOption === SignUpOption.TEAM_PRO
            }
          >
            <TextField
              label={t("signup.orgNameFull")}
              required={
                selectedOption === SignUpOption.PERSONAL_PRO ||
                selectedOption === SignUpOption.TEAM_PRO
              }
              fullWidth
              margin="normal"
              value={requestedOrgName}
              onChange={handleOrgNameChange}
              disabled={isLoading}
              inputProps={{ maxLength: 80 }}
              error={isOrgNameAvailable === false}
              helperText={
                isCheckingOrgName
                  ? t("userMgmt.checking")
                  : isOrgNameAvailable === false
                    ? t("onboarding.notAvailable")
                    : isOrgNameAvailable === true
                      ? t("userMgmt.available")
                      : " "
              }
            />
            <TextField
              label={t("signup.orgNameShort")}
              required={
                selectedOption === SignUpOption.PERSONAL_PRO ||
                selectedOption === SignUpOption.TEAM_PRO
              }
              fullWidth
              margin="normal"
              value={requestedOrgShortName}
              onChange={handleShortNameChange}
              disabled={isLoading}
              inputProps={{ maxLength: 25 }}
              error={isShortNameAvailable === false}
              helperText={
                isCheckingShortName
                  ? t("userMgmt.checking")
                  : isShortNameAvailable === false
                    ? t("onboarding.notAvailable")
                    : isShortNameAvailable === true
                      ? t("userMgmt.available")
                      : " "
              }
            />
            <FormControl component="fieldset" margin="normal" fullWidth>
              <FormLabel component="legend">
                {t("signup.primaryGuideline")}
              </FormLabel>
              <RadioGroup
                row
                value={primaryGuideline}
                onChange={(e) => setPrimaryGuideline(e.target.value)}
              >
                <FormControlLabel
                  value="EUCAST"
                  control={<Radio />}
                  label="EUCAST"
                />
                <FormControlLabel
                  value="CLSI"
                  control={<Radio />}
                  label="CLSI"
                />
              </RadioGroup>
              <Typography variant="caption" color="text.secondary">
                {t("signup.primaryGuidelineHelp")}
              </Typography>
            </FormControl>
          </Collapse>

          {/* Language picker — always visible. For PRO branches this sets
              both the org's preferredLanguage and the admin's; for FREE
              this sets the user's personal preferredLanguage. */}
          <FormControl fullWidth margin="normal" disabled={isLoading}>
            <InputLabel id="signup-org-language-label">
              {t("signup.orgLanguage")}
            </InputLabel>
            <Select
              labelId="signup-org-language-label"
              label={t("signup.orgLanguage")}
              value={orgLanguage}
              onChange={(e) => setOrgLanguage(e.target.value)}
            >
              {SUPPORTED_LANGUAGES.map((lang) => (
                <MenuItem key={lang.code} value={lang.code}>
                  {lang.label}
                </MenuItem>
              ))}
            </Select>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 0.5 }}
            >
              {t("signup.orgLanguageHelp")}
            </Typography>
          </FormControl>

          {/* --- ADDED: Div for the reCAPTCHA widget to render in --- */}
          <div
            id="signup-recaptcha"
            style={{ marginTop: 16, marginBottom: 8 }}
          />

          {error && (
            <Alert severity="error" sx={{ width: "100%", mt: 2 }}>
              {error}
            </Alert>
          )}

          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{ mt: 3, mb: 2 }}
            disabled={isLoading}
          >
            {isLoading ? (
              <CircularProgress size={24} />
            ) : (
              t("signup.submitRequest")
            )}
          </Button>
          <Typography variant="body2" align="center">
            <Link component={RouterLink} to="/login" sx={{ cursor: "pointer" }}>
              {t("signup.alreadyHaveAccount")}
            </Link>
          </Typography>
        </Box>
      </Box>
    </Container>
  );
}

export default SignUp;
